import React, { createContext, useContext, useState, useEffect } from 'react';
import pb from '../lib/pocketbase';

const MessagesContext = createContext();

export const useMessagesData = () => {
    const context = useContext(MessagesContext);
    if (!context) {
        throw new Error('useMessagesData must be used within a MessagesProvider');
    }
    return context;
};

export const MessagesProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    // Initial fetch and Real-time subscription
    useEffect(() => {
        const fetchMessages = async () => {
            try {
                setLoading(true);
                const records = await pb.collection('messages').getFullList({
                    sort: '-created',
                });
                setMessages(records);
            } catch (error) {
                console.error('Error fetching messages:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();

        // Real-time subscription
        pb.collection('messages').subscribe('*', (e) => {
            if (e.action === 'create') {
                setMessages(prev => [e.record, ...prev]);
            } else if (e.action === 'delete') {
                setMessages(prev => prev.filter(msg => msg.id !== e.record.id));
            } else if (e.action === 'update') {
                setMessages(prev => prev.map(msg => msg.id === e.record.id ? e.record : msg));
            }
        });

        return () => {
            pb.collection('messages').unsubscribe('*');
        };
    }, []);

    const addMessage = async (newMessage) => {
        try {
            await pb.collection('messages').create({
                ...newMessage,
                read: false
            });
        } catch (error) {
            console.error('Error adding message:', error);
            throw error;
        }
    };

    const deleteMessage = async (id) => {
        try {
            await pb.collection('messages').delete(id);
        } catch (error) {
            console.error('Error deleting message:', error);
        }
    };

    const markAsRead = async (id) => {
        try {
            await pb.collection('messages').update(id, { read: true });
        } catch (error) {
            console.error('Error marking message as read:', error);
        }
    };

    const value = {
        messages,
        loading,
        addMessage,
        deleteMessage,
        markAsRead
    };

    return (
        <MessagesContext.Provider value={value}>
            {children}
        </MessagesContext.Provider>
    );
};
